import React, { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faTrash, faEdit, faCheckCircle, faTimesCircle, faTicketAlt } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import API from "../../Api/Api";
import Loading from "../../layouts/Loading";

function Coupons() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCoupon, setCurrentCoupon] = useState(null); // For editing

    // Form states
    const [formData, setFormData] = useState({
        code: "",
        discountType: "percentage",
        discountValue: 0,
        isActive: true,
        expiryDate: "",
        maxUses: "",
    });

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const response = await API.get("/coupons");
            setCoupons(response.data);
        } catch (error) {
            console.error("Error fetching coupons:", error);
            Swal.fire("Error", "Failed to load coupons", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (coupon = null) => {
        if (coupon) {
            setCurrentCoupon(coupon);
            setFormData({
                code: coupon.code,
                discountType: coupon.discountType,
                discountValue: coupon.discountValue,
                isActive: coupon.isActive,
                expiryDate: coupon.expiryDate ? new Date(coupon.expiryDate).toISOString().split("T")[0] : "",
                maxUses: coupon.maxUses || "",
            });
        } else {
            setCurrentCoupon(null);
            setFormData({
                code: "",
                discountType: "percentage",
                discountValue: 0,
                isActive: true,
                expiryDate: "",
                maxUses: "",
            });
        }
        setIsModalOpen(true);
    };

    const handleSaveCoupon = async (e) => {
        e.preventDefault();
        try {
            const dataToSave = {
                ...formData,
                discountValue: Number(formData.discountValue),
                maxUses: formData.maxUses === "" ? null : Number(formData.maxUses),
                expiryDate: formData.expiryDate === "" ? null : formData.expiryDate,
            };

            if (currentCoupon) {
                await API.put(`/coupons/${currentCoupon._id}`, dataToSave);
                Swal.fire("Success", "Coupon updated successfully", "success");
            } else {
                await API.post("/coupons", dataToSave);
                Swal.fire("Success", "Coupon created successfully", "success");
            }
            setIsModalOpen(false);
            fetchCoupons();
        } catch (error) {
            const msg = error.response?.data?.message || "Failed to save coupon";
            Swal.fire("Error", msg, "error");
        }
    };

    const handleDeleteCoupon = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This coupon will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await API.delete(`/coupons/${id}`);
                Swal.fire("Deleted!", "Coupon has been deleted.", "success");
                fetchCoupons();
            } catch (error) {
                Swal.fire("Error", "Failed to delete coupon", "error");
            }
        }
    };

    const filteredCoupons = coupons.filter(c =>
        c.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const CouponsContent = (
        <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <FontAwesomeIcon icon={faTicketAlt} className="text-blue-500" />
                        Coupon Management
                    </h1>
                    <p className="text-gray-400 mt-1">Create and manage discount codes for your customers.</p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 shadow-lg shadow-blue-900/20"
                >
                    <FontAwesomeIcon icon={faPlus} />
                    Create Coupon
                </button>
            </div>

            <div className="bg-gray-800 rounded-xl shadow-xl overflow-hidden border border-gray-700">
                <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex items-center">
                    <div className="relative w-full max-w-md">
                        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Search by code..."
                            className="w-full bg-gray-900 border border-gray-700 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="py-20 flex justify-center"><Loading /></div>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-gray-900/50 text-gray-400 text-sm uppercase">
                                <tr>
                                    <th className="px-6 py-4">Code</th>
                                    <th className="px-6 py-4">Discount</th>
                                    <th className="px-6 py-4">Type</th>
                                    <th className="px-6 py-4">Stats</th>
                                    <th className="px-6 py-4">Expiry</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {filteredCoupons.length > 0 ? filteredCoupons.map((coupon) => (
                                    <tr key={coupon._id} className="hover:bg-gray-700/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="bg-blue-900/30 text-blue-400 px-3 py-1 rounded font-mono font-bold border border-blue-800/50">
                                                {coupon.code}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-white font-medium">
                                            {coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `$${coupon.discountValue}`}
                                        </td>
                                        <td className="px-6 py-4 text-gray-400 capitalize">
                                            {coupon.discountType}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm">
                                                <span className="text-white font-semibold">{coupon.usedCount}</span>
                                                <span className="text-gray-500"> / {coupon.maxUses || '∞'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {coupon.expiryDate ? (
                                                <span className={new Date(coupon.expiryDate) < new Date() ? 'text-red-400' : 'text-gray-400'}>
                                                    {new Date(coupon.expiryDate).toLocaleDateString()}
                                                </span>
                                            ) : (
                                                <span className="text-gray-600">No deadline</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {coupon.isActive ? (
                                                <span className="flex items-center gap-1.5 text-green-400 text-sm">
                                                    <FontAwesomeIcon icon={faCheckCircle} /> Active
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1.5 text-gray-500 text-sm">
                                                    <FontAwesomeIcon icon={faTimesCircle} /> Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2 text-lg">
                                                <button
                                                    onClick={() => handleOpenModal(coupon)}
                                                    className="p-1.5 text-blue-400 hover:bg-blue-400/10 rounded transition-all"
                                                    title="Edit"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCoupon(coupon._id)}
                                                    className="p-1.5 text-red-400 hover:bg-red-400/10 rounded transition-all"
                                                    title="Delete"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-20 text-center text-gray-500">
                                            No coupons found. Click "Create Coupon" to get started.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-gray-800 w-full max-w-md rounded-2xl shadow-2xl border border-gray-700 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-gray-900/30">
                            <h2 className="text-xl font-bold text-white">
                                {currentCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                                <FontAwesomeIcon icon={faEdit} className="rotate-45" /> {/* Close icon substitution if needed, but usually just regular button */}
                                <span className="text-2xl">&times;</span>
                            </button>
                        </div>

                        <form onSubmit={handleSaveCoupon} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Coupon Code</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none uppercase font-mono"
                                    value={formData.code}
                                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                    placeholder="e.g. SAVE20"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Type</label>
                                    <select
                                        className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.discountType}
                                        onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                                    >
                                        <option value="percentage">Percentage (%)</option>
                                        <option value="fixed">Fixed Amount ($)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Value</label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        step="0.01"
                                        className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.discountValue}
                                        onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Max Uses</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.maxUses}
                                        onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                                        placeholder="Unlimited"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Expiry Date</label>
                                    <input
                                        type="date"
                                        className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.expiryDate}
                                        onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    className="w-5 h-5 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                                <label htmlFor="isActive" className="text-sm font-medium text-gray-300 pointer-events-none">
                                    Coupon is Active and usable
                                </label>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-blue-900/20"
                                >
                                    {currentCoupon ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );

    return <AdminLayout Content={CouponsContent} />;
}

export default Coupons;
